import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getExtensionValue } from "@medplum/core";
import { MedicationStatement } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Badge } from "@mantine/core";

import * as Sentry from "@sentry/react";
import { isEmpty } from "lodash";

import { Extensions } from "@/fhir/enums/extensions";
import { RootState, useAppDispatch } from "@/store";
import { loadMedication, selectMedicationList } from "@/store/slices/client-medication";
import { getCodeableConceptDisplay, joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

import { ClientBadgesWrapper } from "./ClientBadgesWrapper";
import classes from "./ClientHealthSummary.module.css";
import { ClientHealthSummaryTypeEnum } from "./enums";

type ClientMedicationsProps = {
  noReportedMedication: boolean;
  onClick?: () => void;
};

export default function ClientMedications({
  noReportedMedication,
  onClick,
}: ClientMedicationsProps) {
  const allMedications = useSelector<RootState, MedicationStatement[]>(selectMedicationList);
  const dispatch = useAppDispatch();
  const medplum = useMedplum();
  const { clientId } = useParams() as { clientId: string };

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const medications = await medplum.searchResources("MedicationStatement", {
          subject: joinReferenceString("Patient", clientId),
        });
        dispatch(loadMedication(medications));
      } catch (error) {
        Sentry.captureException(error);
        toast({
          title: "Error fetching medications",
          message: "Please try again later",
          type: "error",
        });
      }
    };
    fetchMedications();
  }, [medplum, clientId, dispatch]);

  const visibleMedications = allMedications.filter((medication) =>
    getExtensionValue(medication, Extensions.DisplayMedicationOnClientSidePanel),
  );
  const filteredMedications = visibleMedications
    .map((medication) => getCodeableConceptDisplay(medication.medicationCodeableConcept))
    .filter(Boolean);

  return (
    <ClientBadgesWrapper
      onClick={onClick}
      title="Medications"
      noInfo={isEmpty(visibleMedications)}
      summaryType={ClientHealthSummaryTypeEnum.Allergy}
      noneReported={noReportedMedication && isEmpty(visibleMedications)}
    >
      {filteredMedications.map((med) => {
        return (
          <Badge
            px="md"
            py="sm"
            key={med}
            tt="none"
            c="black"
            bg="white"
            variant="filled"
            className={classes.patientBadge}
          >
            {med}
          </Badge>
        );
      })}
    </ClientBadgesWrapper>
  );
}
